import type {
  Currency,
  Language,
  Region,
} from '@whoop/i18n/types/internationalization';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  DEFAULT_REGION,
} from '@whoop/i18n/types/internationalization';
import type { CountryCode } from '@whoop/i18n/types/address';
import { DEFAULT_COUNTRY_CODE } from '@whoop/i18n/types/address';
import { createStore } from 'zustand';
import type { Address, PromoCodeState, PromoInfo } from 'ui';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import type { CheckoutPromo } from 'services/generated/membership-service-v2/models/CheckoutPromo';
import { immer } from 'zustand/middleware/immer';
import type { ReceiptOrderStatusResponse } from 'services/generated/commerce-service/models/ReceiptOrderStatusResponse';
import type { CatalogResponse } from 'services/generated/commerce-service/models/CatalogResponse';
import type { CheckoutResponse } from 'services/generated/commerce-service/models/CheckoutResponse';
import type { GiftDetails } from 'services/generated/commerce-service/models/GiftDetails';
import type { ParsedCartDTO } from 'ui/types/cartTypes';
import type { AddressVerificationStatus } from 'ui/types/Address';
import type { EngravingInfo } from 'ui/utils/engravingUtils';
import type { StripePaymentRequest } from 'ui/types/Stripe';
import type { SiteWidePromoContent } from '@/types/siteWidePromoTypes';
import type { PromoType } from '@/lib/utils/initialDataFetcher';
import type { FeatureAvailability } from '@/hooks/featureAvailabilityClass';
import type {
  MembershipStatus,
  MembershipVariants,
} from '@/types/maverickTypes';
import type { Modal } from '@/types/modals';
import type { IBanner } from '@/lib/utils/promoBannerHelpers';

interface ISiteProps {
  ecommCatalog: CatalogResponse;
  asAGift: boolean;
  language: Language;
  region: Region;
  country: CountryCode;
  currency: Currency;
  cartExceedsBatteryLimit: boolean;
  shippingAddress?: Address;
  promoInfo?: PromoInfo;
  promoCode?: PromoCodeState;
  email?: string;
  isCartOpen: boolean;
  isCountrySelectorOpen: boolean;
  hasSeenCurrencyConversionModal: boolean;
  giftMessaging?: GiftDetails;
  // Once fully implemented, remove promoInfo and promoCode and replace everywhere in app with promoContent usage
  // Then this object should no longer be optional
  promoContent?: JoinFlowContent;
  featureFlags: Record<string, boolean>;
  experimentVariants: Record<string, string>;
  returnShippingCost?: number;
  siteWidePromoContent?: SiteWidePromoContent;
  isRafGiftFlow?: boolean;
  cart: ParsedCartDTO;
  checkoutResponse?: CheckoutResponse | ReceiptOrderStatusResponse;
  promoType: PromoType;
  stripePaymentRequest?: StripePaymentRequest;
  addressVerificationStatus: AddressVerificationStatus;
  engravingInfo?: EngravingInfo;
  skipMemSelectFlow?: string;
  stripeAccount: string;
  peakAnnualOriginalPrice: number;
  featureAvailability?: FeatureAvailability;
  membershipStatus: MembershipStatus;
  modals: Record<string, Modal>;
  regionalBanners?: IBanner[];
  isBNPLSelected?: boolean;
  membershipVariants?: MembershipVariants;
  trialDepositPrice?: string;
  showSkipMemSelectToast?: boolean;
}

// Site Actions
interface ISiteActions {
  toggleAsAGift: () => void;
  setAsAGift: (isGift: boolean) => void;
  setFeatureFlags: (featureFlags: Record<string, boolean>) => void;
  initializePromo: (
    promoContent?: JoinFlowContent,
    promo?: CheckoutPromo,
    siteWidePromoContent?: SiteWidePromoContent,
    isRafGiftFlow?: boolean,
  ) => void;
  setShippingAddress: (address: Address) => void;
  setCartExceedsBatteryLimit: (boolean) => void;
  setPromoCode: (promoCode: PromoCodeState) => void;
  setEmail: (email: string | undefined) => void;
  setEcommCatalog: (catalog: CatalogResponse) => void;
  setReturnShippingCost: (returnShippingCost?: number) => void;
  toggleIsCartOpen: () => void;
  setIsCartOpen: (isCartOpen: boolean) => void;
  setHasSeenCurrencyConversionModal: (
    hasSeenCurrencyConversionModal: boolean,
  ) => void;
  setIsCountrySelectorOpen: (isCountrySelectorOpen: boolean) => void;
  setGiftMessaging: (giftMessage: GiftDetails | undefined) => void;
  setExperimentVariant: (experimentName: string, variant: string) => void;
  setIsRafGiftFlow: (isRafGiftFlow: boolean) => void;
  setEcommCart: (cart: ParsedCartDTO) => void;
  setCheckoutResponse: (
    checkoutResponse: CheckoutResponse | ReceiptOrderStatusResponse,
  ) => void;
  setPromoType: (promoType: PromoType) => void;
  setStripePaymentRequest: (
    stripePaymentRequest?: StripePaymentRequest,
  ) => void;
  setAddressVerificationStatus: (status: AddressVerificationStatus) => void;
  setEngravingInfo: (engravingInfo: EngravingInfo) => void;
  setSkipMemSelectFlow: (string) => void;
  setStripeAccount: (stripeAccount: string) => void;
  setPeakAnnualOriginalPrice: (peakMembershipPrice: number) => void;
  setFeatureAvailability: (featureAvailability: FeatureAvailability) => void;
  setMembershipStatus: (membershipStatus: MembershipStatus) => void;
  openModal: (modal: string) => void;
  closeModal: (modal: string) => void;
  setRegionalBanners: (regionalBanners: IBanner[]) => void;
  setIsBNPLSelected: (isBNPLSelected: boolean) => void;
  setMembershipVariants: (membershipVariants: MembershipVariants) => void;
  setTrialDepositPrice: (price: string) => Promise<void>;
  setShowSkipMemSelectToast: (show: boolean) => void;
}

export interface ISiteStore extends ISiteProps, ISiteActions {}

export const DEFAULT_PROPS: ISiteProps = {
  ecommCatalog: { initial_products: [], memberships: [], accessories: [] },
  asAGift: false,
  language: DEFAULT_LANGUAGE,
  region: DEFAULT_REGION,
  country: DEFAULT_COUNTRY_CODE,
  currency: DEFAULT_CURRENCY,
  cartExceedsBatteryLimit: false,
  isCartOpen: false,
  hasSeenCurrencyConversionModal: false,
  isCountrySelectorOpen: false,
  featureFlags: {},
  experimentVariants: {},
  cart: {
    id: null,
    billingDetails: [],
    shippingMethodItems: [],
    cartItemGroups: [],
  },
  promoType: 'None',
  stripePaymentRequest: undefined,
  addressVerificationStatus: 'Default',
  engravingInfo: undefined,
  stripeAccount: 'US',
  peakAnnualOriginalPrice: 0,
  featureAvailability: undefined,
  membershipStatus: {
    isTrialer: false,
    isCanceled: false,
    isElite: false,
    familySize: undefined,
  },
  modals: {},
  isBNPLSelected: false,
  membershipVariants: {},
  showSkipMemSelectToast: true,
};

export const createSiteStore = (initState: ISiteProps = DEFAULT_PROPS) => {
  return createStore<ISiteStore>()(
    immer((set) => ({
      ...initState,
      toggleAsAGift: () =>
        set((state) => ({
          asAGift: !state.asAGift,
        })),
      setAsAGift: (isGift: boolean) =>
        set(() => ({
          asAGift: isGift,
        })),
      setEcommCatalog: (catalog: CatalogResponse) => {
        set((state) => {
          state.ecommCatalog = catalog;
        });
      },
      setReturnShippingCost: (returnShippingCost?: number) => {
        set((state) => {
          state.returnShippingCost = returnShippingCost;
        });
      },
      setFeatureFlags: (featureFlags: Record<string, boolean>) => {
        set((state) => {
          state.featureFlags = {
            ...state.featureFlags,
            ...featureFlags,
          };
        });
      },
      initializePromo: (
        promoContent?: JoinFlowContent,
        promo?: CheckoutPromo,
        siteWidePromoContent?: SiteWidePromoContent,
        isRafGiftFlow?: boolean,
      ) => {
        set((state) => {
          state.promoCode =
            promoContent?.partnership_name || isRafGiftFlow
              ? promoContent?.promo_info
              : promo?.promo_code;
          state.promoContent = promoContent;
          state.promoInfo = promo;
          state.siteWidePromoContent = siteWidePromoContent;
        });
      },
      setShippingAddress: (address: Address) => {
        set((state) => {
          state.shippingAddress = address;
        });
      },
      setCartExceedsBatteryLimit: (hascartExceedsBatteryLimit: boolean) =>
        set((state) => {
          state.cartExceedsBatteryLimit = hascartExceedsBatteryLimit;
        }),
      setPromoCode: (promoCode: PromoCodeState) => {
        set((state) => {
          state.promoCode = promoCode;
        });
      },
      setEmail: (email: string | undefined) => {
        set((state) => {
          state.email = email;
        });
      },
      toggleIsCartOpen: () =>
        set((state) => {
          state.isCartOpen = !state.isCartOpen;
        }),
      setIsCartOpen: (isCartOpen: boolean) => {
        set((state) => {
          state.isCartOpen = isCartOpen;
        });
      },
      setIsCountrySelectorOpen: (isCountrySelectorOpen: boolean) => {
        set((state) => {
          state.isCountrySelectorOpen = isCountrySelectorOpen;
        });
      },
      setHasSeenCurrencyConversionModal: (
        hasSeenCurrencyConversionModal: boolean,
      ) => {
        set((state) => {
          state.hasSeenCurrencyConversionModal = hasSeenCurrencyConversionModal;
        });
      },
      setGiftMessaging: (giftMessage: GiftDetails | undefined) => {
        set((state) => {
          state.giftMessaging = giftMessage;
        });
      },
      setExperimentVariant: (experimentName: string, variant: string) => {
        set((state) => {
          state.experimentVariants = {
            ...state.experimentVariants,
            [experimentName]: variant,
          };
        });
      },
      setIsRafGiftFlow: (isRafGift: boolean) => {
        set((state) => {
          state.isRafGiftFlow = isRafGift;
          state.asAGift = isRafGift;
        });
      },
      setEcommCart: (cart: ParsedCartDTO) => {
        set((state) => {
          state.cart = cart;
        });
      },
      setCheckoutResponse: (
        checkoutResponse: CheckoutResponse | ReceiptOrderStatusResponse,
      ) => {
        set((state) => {
          state.checkoutResponse = checkoutResponse;
        });
      },
      setPromoType: (promoType: PromoType) => {
        set((state) => {
          state.promoType = promoType;
        });
      },
      setStripePaymentRequest: (
        stripePaymentRequest?: StripePaymentRequest,
      ) => {
        set((state) => {
          state.stripePaymentRequest = stripePaymentRequest;
        });
      },
      setAddressVerificationStatus: (status: AddressVerificationStatus) => {
        set((state) => {
          state.addressVerificationStatus = status;
        });
      },
      setEngravingInfo: (engravingInfo: EngravingInfo) => {
        set((state) => {
          state.engravingInfo = engravingInfo;
        });
      },
      setSkipMemSelectFlow: (skip: string) => {
        set((state) => {
          state.skipMemSelectFlow = skip;
        });
      },
      setStripeAccount: (stripeAccount: string) => {
        set((state) => {
          state.stripeAccount = stripeAccount;
        });
      },
      setPeakAnnualOriginalPrice: (peakMembershipPrice: number) => {
        set((state) => {
          state.peakAnnualOriginalPrice = peakMembershipPrice;
        });
      },
      setFeatureAvailability: (featureAvailability: FeatureAvailability) => {
        set((state) => {
          state.featureAvailability = featureAvailability;
        });
      },
      setMembershipStatus: (membershipStatus: MembershipStatus) => {
        set((state) => {
          state.membershipStatus = membershipStatus;
        });
      },
      openModal: (modal: string) => {
        set((state) => {
          state.modals = {
            ...state.modals,
            [modal]: { ...(state.modals[modal] ?? {}), isOpen: true },
          };
        });
      },
      closeModal: (modal: string) => {
        set((state) => {
          state.modals = {
            ...state.modals,
            [modal]: {
              ...(state.modals[modal] ?? {}),
              isOpen: false,
              seen: true,
            },
          };
        });
      },
      setRegionalBanners: (regionalBanners: IBanner[]) => {
        set((state) => {
          state.regionalBanners = regionalBanners;
        });
      },
      setIsBNPLSelected: (isBNPLSelected: boolean) => {
        set((state) => {
          state.isBNPLSelected = isBNPLSelected;
        });
      },
      setMembershipVariants: (membershipVariants: MembershipVariants) => {
        set((state) => {
          state.membershipVariants = membershipVariants;
        });
      },
      setTrialDepositPrice: async (price: string) => {
        set((state) => {
          state.trialDepositPrice = price;
        });
      },
      setShowSkipMemSelectToast: (show: boolean) => {
        set((state) => {
          state.showSkipMemSelectToast = show;
        });
      },
    })),
  );
};
